import moment from "moment";
import React, { useState } from "react";
import { Card, CardBody, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button, Offcanvas, OffcanvasHeader, OffcanvasBody, FormGroup, Label, Input } from "reactstrap";
import Swal from "sweetalert2";
import Spinner from "../../../common/spinner/spinner";
import { AdminService } from "../../../services/AdminService";
import Select from "react-select";

interface TherapistRequestTabProps {
  // Props from parent component
  initialFeedbacks?: any[];
}

const TherapistRequestTab: React.FC<TherapistRequestTabProps> = ({ initialFeedbacks = [] }) => {
  const [sessionFeedbackNeedCall, setSessionFeedbackNeedCall] = useState<any[]>(initialFeedbacks);
  const [offsetNeedCall, setOffsetNeedCall] = useState(1);
  const [userCountNeedCall, setUserCountNeedCall] = useState(20);
  const [isLoadingNeedCall, setIsLoadingNeedCall] = useState(true);

  // State for transcript modal
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState<string[]>([]);

  // State for comment modal
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState("");

  // Filter states
  const [isFilterOptions, setFilterOptions] = useState(false);
  const [isSearchableString, setIsSearchableString] = useState("");
  const [selectedTherapist, setSelectedTherapist] = useState("");
  const [therapistOptions, setTherapistOptions] = useState<any[]>([]);

  const LIMIT = 20;

  React.useEffect(() => {
    getAllFeedbacksNeedCall();
    loadTherapistOptions();
  }, []);

  const loadTherapistOptions = () => {
    // Load therapist options for filter dropdown
    AdminService.getAllTherapistsSimple().then(res => {
      if (res.success && res.data) {
        const options = res.data.map((therapist: any) => ({
          value: therapist._id,
          label: therapist.name
        }));
        setTherapistOptions([{ value: "", label: "All Therapists" }, ...options]);
      }
    });
  };

  const toggleTranscriptModal = () => {
    setTranscriptModalOpen(!transcriptModalOpen);
  };

  const toggleCommentModal = () => {
    setCommentModalOpen(!commentModalOpen);
  };

  const openCommentModal = (comment: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentComment(comment || "No comment available");
    setCommentModalOpen(true);
  };

  const openTranscriptModal = (transcript: string[] | string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row toggle
    
    // Handle both string and array formats
    if (Array.isArray(transcript)) {
      setCurrentTranscript(transcript);
    } else if (typeof transcript === 'string') {
      setCurrentTranscript([transcript]);
    } else {
      setCurrentTranscript(["No transcript available"]);
    }
    
    setTranscriptModalOpen(true);
  };

  const copyToClipboard = (text: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Ngu0103n viu1ec7c lan truyu1ec1n su1ef1 kiu1ec7n click u0111u1ebfn hu00e0ng
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        title: "Copied!",
        text: "Meeting ID copied to clipboard",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
    }).catch(err => {
      console.error('Could not copy text: ', err);
      Swal.fire({
        title: "Error",
        text: "Could not copy to clipboard",
        icon: "error",
        timer: 1500,
        showConfirmButton: false
      });
    });
  };

  const toggleReadStatus = (feedbackId: string, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row toggle
    
    // Show loading state
    Swal.fire({
      title: "Processing...",
      text: "Updating status",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    // Use AdminService to update read status - Using different API endpoint for Therapist tab
    AdminService.updateSessionTherapistFeedbackIsRead(feedbackId, !currentStatus)
      .then(res => {
        if (res.success) {
          // Update the local state after successful API call
          const updatedFeedback = sessionFeedbackNeedCall.map(item => {
            if (item._id === feedbackId) {
              return { ...item, is_read: !currentStatus };
            }
            return item;
          });
          setSessionFeedbackNeedCall(updatedFeedback);
          
          // Show success message
          Swal.fire({
            title: currentStatus ? "Marked as Unread" : "Marked as Read",
            icon: "success",
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          // Show error message
          Swal.fire({
            title: "Error",
            text: res.message || "Failed to update status",
            icon: "error",
            timer: 2000
          });
        }
      })
      .catch(error => {
        console.error("Error updating read status:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to update status. Please try again.",
          icon: "error",
          timer: 2000
        });
      });
  };

  const getAllFeedbacksNeedCall = () => {
    setIsLoadingNeedCall(true);
    const filterData = {
      searchableString: isSearchableString,
      therapistId: selectedTherapist
    };
    // Using different API endpoint for Therapist tab
    AdminService.getAllSessionTherapistFeedbackNeedCall(LIMIT, offsetNeedCall, filterData).then(res => {
      if(res.success){
        setSessionFeedbackNeedCall(res.data);
        setUserCountNeedCall(res.data.length);
        setIsLoadingNeedCall(false);
      } else {
        setSessionFeedbackNeedCall([]);
        setIsLoadingNeedCall(false)
      }
    });
  };

  const seeMoreNeedCall = () => {
    const updatedSkip = offsetNeedCall + 1;
    setOffsetNeedCall(updatedSkip);
    setIsLoadingNeedCall(true);
    const filterData = {
      searchableString: isSearchableString,
      therapistId: selectedTherapist
    };
    // Using different API endpoint for Therapist tab
    AdminService.getAllSessionTherapistFeedbackNeedCall(LIMIT, updatedSkip, filterData).then(res => {
      if (res.success) {
        res.data.map((feedback: any) => {
          setSessionFeedbackNeedCall(feedbacks => [...feedbacks, feedback]);
        });
        setUserCountNeedCall(res.data.length);
      }
      setIsLoadingNeedCall(false);
    });
  };

  const toggleFilter = () => {
    setFilterOptions(!isFilterOptions);
  };

  const handleTherapistChange = (selectedOption: any) => {
    setSelectedTherapist(selectedOption ? selectedOption.value : "");
  };

  const filterFeedbacks = () => {
    setOffsetNeedCall(1);
    getAllFeedbacksNeedCall();
    toggleFilter();
  };

  const resetFilter = () => {
    setIsSearchableString("");
    setSelectedTherapist("");
    setOffsetNeedCall(1);
    // Call getAllFeedbacksNeedCall without filter data
    setIsLoadingNeedCall(true);
    AdminService.getAllSessionTherapistFeedbackNeedCall(LIMIT, 1).then(res => {
      if(res.success){
        setSessionFeedbackNeedCall(res.data);
        setUserCountNeedCall(res.data.length);
        setIsLoadingNeedCall(false);
      } else {
        setSessionFeedbackNeedCall([]);
        setIsLoadingNeedCall(false)
      }
    });
    toggleFilter();
  };



  return (
    <>
      <Card className="m-h">
        <CardBody>
          <Row>
            <Col className="d-flex admin-filter">
              <button className="btn btn-sm mr-2" onClick={toggleFilter}>
                Filter <i className="bx bx-filter-alt"></i>
              </button>
            </Col>
          </Row>
          <Row>
            <Col sm="12" className="mt10">
              {sessionFeedbackNeedCall !== undefined && sessionFeedbackNeedCall.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table">
                      <thead className="thead-dark">
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Therapist</th>
                          <th scope="col">Client</th>
                          <th scope="col">Session Date</th>
                          <th scope="col">Therapist Phone</th>
                          <th scope="col">Transcript</th>
                          <th scope="col">Meeting ID</th>
                          <th scope="col">View Comment</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessionFeedbackNeedCall &&
                          sessionFeedbackNeedCall.map((feedback: any, index: number) => (
                            <React.Fragment key={feedback?._id}>
                                <tr>
                                <th scope="row">{index + 1}</th>
                                <td>
                                  {feedback?.therapistDetails?.firstname} {feedback?.therapistDetails?.lastname}
                                </td>
                                <td>
                                  {feedback?.clientDetails?.firstname} {feedback?.clientDetails?.lastname}
                                </td>
                                <td>
                                  {moment(feedback?.meetingDetails?.createdAt).format("YYYY-MM-DD hh:mm A")}
                                </td>
                                <td>
                                  {feedback?.therapistDetails?.primaryPhone || "N/A"}
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-link text-warning"
                                    onClick={(e) => openTranscriptModal(feedback?.transcriptionText || ["No transcript available"], e)}
                                  >
                                    <i className="fa fa-file-text-o mr-1"></i> View Transcript
                                  </button>
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-sm btn-primary" 
                                    onClick={(e) => copyToClipboard(feedback?.meetingId, e)}
                                    title="Copy Meeting ID"
                                  >
                                    <i className="fa fa-copy mr-1"></i> Copy
                                  </button>
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-sm btn-info" 
                                    onClick={(e) => openCommentModal(feedback?.comments, e)}
                                    title="View Comment"
                                    disabled={!feedback?.comments}
                                  >
                                    <i className="fa fa-comment mr-1"></i> View Comment
                                  </button>
                                </td>
                                <td>
                                  <button 
                                    className={`btn btn-sm ${feedback?.is_read ? 'btn-secondary' : 'btn-success'}`}
                                    onClick={(e) => toggleReadStatus(feedback?._id, feedback?.is_read || false, e)}
                                    title={feedback?.is_read ? "Mark as Unread" : "Mark as Read"}
                                  >
                                    <i className={`fa ${feedback?.is_read ? 'fa-envelope' : 'fa-envelope-open'} mr-1`}></i>
                                    {feedback?.is_read ? "Mark as Unread" : "Mark as Read"}
                                  </button>
                                </td>
                              </tr>

                            </React.Fragment>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  {userCountNeedCall >= LIMIT && (
                    <>
                      <button className="btn btn-warning text-center mt-4" onClick={seeMoreNeedCall}>
                        See More
                      </button>
                    </>
                  )}
                  {userCountNeedCall == 0 && <span>Therapist requests not found....</span>}
                </>
              ) : (
                <>
                  {isLoadingNeedCall ? <Spinner /> : <div>Therapist requests not found.</div>}</>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Transcript Modal */}
      <Modal isOpen={transcriptModalOpen} toggle={toggleTranscriptModal} size="lg">
        <ModalHeader toggle={toggleTranscriptModal}>
          Session Transcript
        </ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {currentTranscript.length > 0 ? (
              <div>
                {currentTranscript.map((line, index) => (
                  <p key={index} style={{ marginBottom: '8px' }}>
                    <span style={{ color: '#777', marginRight: '10px' }}>{index + 1}.</span>
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p>No transcript available</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleTranscriptModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Comment Modal */}
      <Modal isOpen={commentModalOpen} toggle={toggleCommentModal} size="md">
        <ModalHeader toggle={toggleCommentModal}>
          Therapist Comment
        </ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {currentComment ? (
              <p style={{ whiteSpace: 'pre-wrap' }}>{currentComment}</p>
            ) : (
              <p>No comment available</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCommentModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Filter Offcanvas */}
      <Offcanvas className="offcanvas-end2" isOpen={isFilterOptions} direction="end" toggle={toggleFilter}>
        <OffcanvasHeader toggle={toggleFilter}>Filter Therapist Request</OffcanvasHeader>
        <OffcanvasBody>
          <Row className="mt-4">
            <Col lg={12}>
              <FormGroup>
                <Label>Search by Client Name, Email or Phone</Label>
                <Input
                  id="search"
                  name="text"
                  placeholder="Search Users (ex: first/last name, email or phone)"
                  type="text"
                  onKeyDown={(e) => e.key === 'Enter' && filterFeedbacks()}
                  onChange={e => setIsSearchableString(e.target.value)}
                  value={isSearchableString}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col lg={12}>
              <FormGroup>
                <Label>Search by Therapist Name</Label>
                <Select
                  id="therapistName"
                  options={therapistOptions}
                  onChange={handleTherapistChange}
                  value={therapistOptions.find(option => option.value === selectedTherapist)}
                  placeholder="Select Therapist"
                  isClearable
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-5">
            <Col lg={6}>
              <button
                className={`btn ${isLoadingNeedCall ? "btn-primary" : "btn-success"} w-100`}
                onClick={filterFeedbacks}
                disabled={isLoadingNeedCall}
              >
                {isLoadingNeedCall ? "Loading..." : "Search"}
              </button>
            </Col>
            <Col lg={6}>
              <button
                className="btn btn-secondary w-100"
                onClick={resetFilter}
                disabled={isLoadingNeedCall}
              >
                Reset
              </button>
            </Col>
          </Row>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default TherapistRequestTab;
